/**
 * Building blocks for world map scenes: a seeded random generator, ridge / hill bands and small
 * props (trees, pines, bushes, flowers, rocks, clouds, stars). All return core figure layers, so
 * scenes are painted by the same `Figure` renderer (same ink, outline and shade rules).
 */
import type { Layer } from '../Figure';
import { blob, ellipse, poly, scallop } from '../geom';

export function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const r2 = (n: number) => Math.round(n * 100) / 100;

/** A gentle ridge across the whole width, closed down to `bottom`. */
export function ridge(w: number, y: number, amp: number, step: number, seed: number, bottom: number): string {
  const R = rng(seed);
  const pts: [number, number][] = [];
  for (let x = -60; x <= w + 60 + step; x += step) pts.push([r2(x), r2(y + (R() - 0.5) * 2 * amp)]);
  return `${blob(pts, false, 0.9)}L${r2(w + 80)} ${r2(bottom)}L-80 ${r2(bottom)}Z`;
}

/** Evenly spread x positions with jitter (deterministic). */
export function spread(x0: number, x1: number, gap: number, seed: number, jitter = 0.4): number[] {
  const R = rng(seed);
  const out: number[] = [];
  for (let x = x0 + gap * R() * 0.5; x < x1; x += gap * (1 - jitter / 2 + R() * jitter)) out.push(r2(x));
  return out;
}

export const roundTree = (x: number, y: number, s: number, crown = 'tree', trunk = 'trunk', line = true): Layer[] => [
  { k: 'region', d: poly([[x - 3.4 * s, y], [x - 2.6 * s, y - 18 * s], [x + 2.6 * s, y - 18 * s], [x + 3.4 * s, y]]), fill: trunk, shade: false, line },
  { k: 'region', d: scallop(x, y - 30 * s, 16 * s, 15 * s, 7, -0.4), fill: crown, shade: 6 * s, line, rim: true },
];

export const pine = (x: number, y: number, s: number, fill = 'pine', trunk = 'trunk', line = true): Layer[] => [
  { k: 'region', d: poly([[x - 2.4 * s, y], [x - 2.4 * s, y - 9 * s], [x + 2.4 * s, y - 9 * s], [x + 2.4 * s, y]]), fill: trunk, shade: false, line },
  {
    k: 'region',
    d: blob([[x, y - 50 * s], [x + 9 * s, y - 34 * s], [x + 6 * s, y - 33 * s], [x + 14 * s, y - 20 * s], [x + 9 * s, y - 19.5 * s], [x + 17 * s, y - 8 * s], [x - 17 * s, y - 8 * s], [x - 9 * s, y - 19.5 * s], [x - 14 * s, y - 20 * s], [x - 6 * s, y - 33 * s], [x - 9 * s, y - 34 * s]], true, 0.25),
    fill,
    shade: 5 * s,
    line,
  },
];

export const bush = (x: number, y: number, s: number, fill = 'bush', line = true): Layer[] => [
  { k: 'region', d: `${scallop(x, y - 9 * s, 22 * s, 12 * s, 6, 0.2)}`, fill, shade: 5 * s, rim: true, line },
];

export const flower = (x: number, y: number, petal: string, centre: string, s = 1): Layer[] => [
  { k: 'ink', d: `M${x} ${y + 3 * s}C${x} ${y + 7 * s} ${x - 1} ${y + 10 * s} ${x} ${y + 13 * s}`, w: 0.5, color: 'stem' },
  { k: 'spot', d: scallop(x, y, 3.6 * s, 3.6 * s, 5, 0, 1.8), fill: petal, stage: 2 },
  { k: 'spot', d: ellipse(x, y, 1.4 * s, 1.4 * s), fill: centre, stage: 2 },
];

export const rock = (x: number, y: number, s: number, fill = 'rock', seed = 1): Layer[] => {
  const R = rng(seed);
  const pts: [number, number][] = [];
  for (let i = 0; i < 7; i++) {
    const a = Math.PI + (i / 6) * Math.PI;
    const rr = 1 - R() * 0.25;
    pts.push([r2(x + Math.cos(a) * 18 * s * rr), r2(y + Math.min(0, Math.sin(a)) * 13 * s * rr)]);
  }
  return [{ k: 'region', d: blob(pts, true, 0.7), fill, shade: 5 * s, rim: true }];
};

export const cloud = (x: number, y: number, s: number, fill = 'cloud', line = false): Layer[] => [
  { k: 'region', d: scallop(x, y, 30 * s, 11 * s, 6, 0.3), fill, shade: 3 * s, line },
];

export const twinkle = (x: number, y: number, r: number, fill: string): Layer => ({
  k: 'spot',
  d: `M${x} ${y - r}C${x + r * 0.15} ${y - r * 0.15} ${x + r * 0.15} ${y - r * 0.15} ${x + r} ${y}C${x + r * 0.15} ${y + r * 0.15} ${x + r * 0.15} ${y + r * 0.15} ${x} ${y + r}C${x - r * 0.15} ${y + r * 0.15} ${x - r * 0.15} ${y + r * 0.15} ${x - r} ${y}C${x - r * 0.15} ${y - r * 0.15} ${x - r * 0.15} ${y - r * 0.15} ${x} ${y - r}Z`,
  fill,
  stage: 2,
});

export const grass = (x: number, y: number, s = 1): Layer[] => [
  { k: 'ink', d: `M${x} ${y}C${x - 1} ${y - 5 * s} ${x - 2} ${y - 8 * s} ${x - 4 * s} ${y - 11 * s}`, w: 0.55, color: 'grassInk', taper: 'end' },
  { k: 'ink', d: `M${x + 3 * s} ${y}C${x + 3 * s} ${y - 6 * s} ${x + 4 * s} ${y - 9 * s} ${x + 7 * s} ${y - 12 * s}`, w: 0.55, color: 'grassInk', taper: 'end' },
];

/** Full-bleed rectangle (scene units). */
export const rect = (x: number, y: number, w: number, h: number) => poly([[x, y], [x + w, y], [x + w, y + h], [x, y + h]]);
/** Rounded rectangle. */
export function rrect(x: number, y: number, w: number, h: number, r: number): string {
  const k = r * 0.45;
  return `M${x + r} ${y}L${x + w - r} ${y}C${x + w - k} ${y} ${x + w} ${y + k} ${x + w} ${y + r}L${x + w} ${y + h - r}C${x + w} ${y + h - k} ${x + w - k} ${y + h} ${x + w - r} ${y + h}L${x + r} ${y + h}C${x + k} ${y + h} ${x} ${y + h - k} ${x} ${y + h - r}L${x} ${y + r}C${x} ${y + k} ${x + k} ${y} ${x + r} ${y}Z`;
}
